import { WorkflowActionId } from "@sp-crm/core";
import { AdvancedSearchEntityType, WorkflowStatus } from "generated/graphql";
import { actionsKey, WorkflowBlueprint, WorkflowTriggerKind } from "./workflow-blueprint";

const UNRECOGNIZED = "unrecognized";

export interface WorkflowEventSource {
    id: string;
    attachTo: AdvancedSearchEntityType;
    status: WorkflowStatus;
}

export type WorkflowEventProperties = {
    workflowId: string;
    entityType: AdvancedSearchEntityType;
    status: WorkflowStatus;
    triggerKind: WorkflowTriggerKind | typeof UNRECOGNIZED;
    action: string;
    actionCount: number;
};

export function workflowEventProperties(
    workflow: WorkflowEventSource,
    blueprint: WorkflowBlueprint | null,
): WorkflowEventProperties {
    return {
        workflowId: workflow.id,
        entityType: workflow.attachTo,
        status: workflow.status,
        triggerKind: blueprint ? blueprint.triggerKind : UNRECOGNIZED,
        action: blueprint ? actionsKey(blueprint.actions) : UNRECOGNIZED,
        actionCount: blueprint ? blueprint.actions.length : 0,
    };
}

export type WizardStepArrival = "open" | "stepper" | "continue" | "back" | "blocker";

export type WizardStepArrivalEvent = {
    wizardStep: string;
    arrivedBy: WizardStepArrival;
};

export type WizardStepViewEventProperties = WorkflowEventProperties &
    WizardStepArrivalEvent;

export function wizardStepViewEventProperties(
    workflow: WorkflowEventSource,
    blueprint: WorkflowBlueprint | null,
    arrival: WizardStepArrivalEvent,
): WizardStepViewEventProperties {
    return {
        ...workflowEventProperties(workflow, blueprint),
        ...arrival,
    };
}

export type WorkflowListEventProperties = {
    workflowCount: number;
    publishedCount: number;
    draftCount: number;
    entityTypes: AdvancedSearchEntityType[];
};

export function workflowListEventProperties(
    workflows: readonly { attachTo: AdvancedSearchEntityType; status: WorkflowStatus }[],
): WorkflowListEventProperties {
    const entityTypes = workflows.map(workflow => workflow.attachTo);
    return {
        workflowCount: workflows.length,
        publishedCount: workflows.filter(
            workflow => workflow.status === WorkflowStatus.Published,
        ).length,
        draftCount: workflows.filter(workflow => workflow.status === WorkflowStatus.Draft)
            .length,
        entityTypes: entityTypes.filter(
            (entityType, index) => entityTypes.indexOf(entityType) === index,
        ),
    };
}

export type BlueprintField = "recordType" | "trigger" | "action" | "actions";

export type BlueprintActionsOperation = "add" | "remove" | "move";

export type BlueprintChangeEventProperties = {
    workflowId: string;
    field: BlueprintField | null;
    fromEntityType: AdvancedSearchEntityType;
    toEntityType: AdvancedSearchEntityType;
    fromTriggerKind: WorkflowTriggerKind;
    toTriggerKind: WorkflowTriggerKind;
    fromAction: string;
    toAction: string;
    fromActionCount: number;
    toActionCount: number;
    operation: BlueprintActionsOperation | null;
};

const actionIds = (blueprint: WorkflowBlueprint): WorkflowActionId[] =>
    blueprint.actions.map(entry => entry.id);

const sameIds = (
    a: readonly WorkflowActionId[],
    b: readonly WorkflowActionId[],
): boolean => a.length === b.length && a.every((id, index) => id === b[index]);

function actionsOperation(
    from: WorkflowBlueprint,
    to: WorkflowBlueprint,
): BlueprintActionsOperation | null {
    const fromIds = actionIds(from);
    const toIds = actionIds(to);
    if (toIds.length > fromIds.length) {
        return "add";
    }
    if (toIds.length < fromIds.length) {
        return "remove";
    }
    return sameIds(fromIds, toIds) ? null : "move";
}

function changedField(
    from: WorkflowBlueprint,
    to: WorkflowBlueprint,
): BlueprintField | null {
    if (from.entityType !== to.entityType) {
        return "recordType";
    }
    if (from.triggerKind !== to.triggerKind) {
        return "trigger";
    }
    if (actionsOperation(from, to)) {
        return "actions";
    }
    if (actionsKey(from.actions) !== actionsKey(to.actions)) {
        return "action";
    }
    return null;
}

export function blueprintChangeEventProperties(
    workflowId: string,
    from: WorkflowBlueprint,
    to: WorkflowBlueprint,
): BlueprintChangeEventProperties {
    return {
        workflowId,
        field: changedField(from, to),
        fromEntityType: from.entityType,
        toEntityType: to.entityType,
        fromTriggerKind: from.triggerKind,
        toTriggerKind: to.triggerKind,
        fromAction: actionsKey(from.actions),
        toAction: actionsKey(to.actions),
        fromActionCount: from.actions.length,
        toActionCount: to.actions.length,
        operation: actionsOperation(from, to),
    };
}
